import React from 'react';
import { isEqual } from 'lodash-es';

import { useAppDispatch, useAppSelector } from '@/store';
import { SessionStatus, sessionCheck, sessionLoad, selectStaleUnpaidSession } from '@/store/slices/session';
import { userBrandsList } from '@/store/slices/casino-instance';

import type { ImportantNowTone } from '../../../../theme/tokens';
import type { SpotlightPickItem } from '../Spotlight';

export type ImportantNoticeKind =
  | 'payment-failed'
  | 'payment-processing'
  | 'casino-setup'
  | 'configurator-unpaid'
  | 'content-generating'
  | 'feature-payment'
  | 'blog-new-articles'
  | 'blog-credits'
  | 'ftux-pick';

/** A pending feature-upgrade invoice surfaced as a premium Spotlight card: everything the card
 *  needs to render (icon, name, tier, price) plus where to send the owner to finish paying. Built
 *  by {@link usePendingUpgradeNotices} from the owner's open invoices + the configurator catalog. */
export type PendingUpgradePayment = {
  /** Catalog item id — the product-detail route param the "Finish payment" CTA navigates to. */
  itemId: number;
  /** The pending invoice being settled. */
  invoiceId: string;
  /** Feature display name (localized catalog name, falling back to the invoice's). */
  name: string;
  /** Feature artwork URL (catalog art bundle logo); '' when the catalog hasn't resolved one. */
  image: string;
  /** The tier the upgrade activates ("Pro"). */
  tierName: string;
  /** Pre-formatted price with currency, e.g. "$59.99 USD". */
  priceText: string;
};

export type ImportantNotice = {
  kind: ImportantNoticeKind;
  tone: ImportantNowTone;
  /** Customizer-entered name — only known once the owner reached the naming step. */
  brandName?: string;
  /** Epoch ms the website was purchased (the brand's `created_at`), when known. */
  purchasedAt?: number;
  /** Whether the stale unpaid session may be cancelled (gates the Cancel button). */
  cancellable?: boolean;
  /** Set only for `feature-payment`: the pending feature upgrade to finish paying (the premium
   *  card owns its own chrome, so `tone` is unused for this kind). */
  payment?: PendingUpgradePayment;
  /** Set only for `blog-new-articles`: the Autopilot articles the blog published since the owner
   *  last cleared the notice, newest first. Their ids are what the blog list marks "New" once the
   *  owner presses "See articles". Keyed by the brand they belong to (see {@link useBlogNotices}). */
  blogArticles?: { brandId: string; ids: string[] };
  /** Set only for `blog-credits`: the credits the refused Autopilot run needed, for the brand it
   *  belongs to. The card's "Top up" primes the buy-credits sheet with it. */
  blogCredits?: { brandId: string; needed: number };
  /** Set only for `ftux-pick`: Lumio's pick for the FTUX piece that waits, as the Spotlight draws it (built by
   *  useFtuxSpotlight in features/ftux/spotlight from the FTUX slice: its copy, its button and Dismiss). */
  ftuxPick?: SpotlightPickItem;
};

const parseMs = (iso: string | undefined): number | undefined => {
  if (!iso) return undefined;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? undefined : ms;
};

/** The most recently created brand — the just-purchased one when the owner is fresh. */
const newestBrand = <T extends { created_at: string }>(list: readonly T[]): T | undefined =>
  list.reduce<T | undefined>((newest, brand) => {
    const ms = parseMs(brand.created_at);
    if (ms === undefined) return newest;
    const newestMs = newest ? parseMs(newest.created_at) : undefined;
    return newestMs === undefined || ms > newestMs ? brand : newest;
  }, undefined);

/**
 * Derives the home "Important Now" notices from real state — returns `[]` when
 * nothing is pressing (so the banner hides entirely):
 *
 *  - `payment-failed` / `payment-processing` ← the configurator session's status.
 *    The backend advances the session as the Stripe PaymentIntent settles
 *    (processing → successful, or → failed); see {@link SessionStatus}.
 *  - `casino-setup` ← the session is in customization (paid, not yet live): the
 *    owner still has steps to finish. `brandName` is only set once they name it in
 *    the customizer; `purchasedAt` comes from the newest brand's `created_at`, so a
 *    brand-new instance can read "the website you purchased N hours ago".
 *  - `content-generating` ← the *selected* brand's content deployment is running
 *    (`content_deployment_in_progress`): Lumio is generating its content. This is a
 *    DISTINCT, automated phase — not the owner-driven customization above.
 *
 * The mechanism is self-contained and API-driven: it loads the session + brand
 * list itself (below), so the notices are built from data — never as a side effect
 * of the owner having passed through a route (configurator/customizer) that happens
 * to load the session. The payment notices stay dormant (no false positives) until
 * the session is loaded with one of those statuses.
 */
export const useImportantNotices = (): ImportantNotice[] => {
  const dispatch = useAppDispatch();
  const status = useAppSelector((s) => s.session.status);
  const brandName = useAppSelector((s) => s.session.data.customization_data?.brandName);
  const brands = useAppSelector((s) => s.casinoInstance.brands, isEqual);
  const sessionLoaded = useAppSelector((s) => s.session.loaded);
  const sessionLoading = useAppSelector((s) => s.session.loading);
  const brandsLoaded = useAppSelector((s) => s.casinoInstance.loaded);
  const brandsLoading = useAppSelector((s) => s.casinoInstance.loading);
  // A half-finished, unpaid configurator session (status `configuration`) the owner
  // left behind — probed via the idempotent `/check` so it never mints a session.
  const staleUnpaid = useAppSelector(selectStaleUnpaidSession, isEqual);
  const staleLoaded = useAppSelector((s) => s.session.stale.loaded);
  const staleLoading = useAppSelector((s) => s.session.stale.loading);

  // Pull the session + brand list straight from the APIs so the banner reflects
  // server state on any entry to Home — not only after visiting a route that loads
  // them. One-shot per mount: both slices reset `loaded`+`loading` to false on a
  // rejected fetch, so a bare `!loaded && !loading` effect keyed on them would
  // re-dispatch forever; the ref makes this fire at most once. Each dispatch is
  // still guarded so it never duplicates an in-flight/finished load from elsewhere.
  const requested = React.useRef(false);
  React.useEffect(() => {
    if (requested.current) return;
    requested.current = true;
    if (!sessionLoaded && !sessionLoading) dispatch(sessionLoad({ ambient: true }));
    if (!brandsLoaded && !brandsLoading) dispatch(userBrandsList());
    if (!staleLoaded && !staleLoading) dispatch(sessionCheck());
  }, [dispatch, sessionLoaded, sessionLoading, brandsLoaded, brandsLoading, staleLoaded, staleLoading]);

  return React.useMemo(() => {
    const notices: ImportantNotice[] = [];

    if (status === SessionStatus.payment_failed) {
      notices.push({ kind: 'payment-failed', tone: 'critical' });
    }
    if (status === SessionStatus.payment_processing) {
      notices.push({ kind: 'payment-processing', tone: 'info' });
    }

    // Owner started building a website but never completed checkout (unpaid,
    // status `configuration`). Offer to resume it or cancel it. This is distinct
    // from `casino-setup` below, which is the PAID-but-not-yet-live state.
    if (staleUnpaid) {
      notices.push({
        kind: 'configurator-unpaid',
        tone: 'action',
        cancellable: staleUnpaid.cancellable,
      });
    }

    // Owner still owes setup steps (paid, customization unfinished).
    if (status === SessionStatus.customization || status === SessionStatus.payment_successful) {
      notices.push({
        kind: 'casino-setup',
        tone: 'action',
        brandName: brandName ?? undefined,
        purchasedAt: parseMs(newestBrand(brands)?.created_at),
      });
    }

    return notices;
  }, [status, brandName, brands, staleUnpaid]);
};
